import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { TrialDialog } from "@/components/TrialDialog";
import { CookieBanner } from "@/components/CookieBanner";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import { BRAND } from "@/data/units";

function NotFoundComponent() {
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-5">
      <div className="max-w-lg">
        <span className="eyebrow">Erro 404</span>
        <h1 className="display mt-4 text-5xl sm:text-6xl">
          Essa página
          <br />
          saiu do treino.
        </h1>
        <p className="mt-5 text-sm text-muted-foreground">
          O endereço que você acessou não existe ou foi movido. Continue pelo método ou escolha
          uma das unidades Nook.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/"
            className="inline-flex h-12 items-center rounded-sm bg-nook-green px-6 text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-nook-green-foreground"
          >
            Voltar ao início
          </Link>
          <Link
            to="/unidades"
            className="inline-flex h-12 items-center rounded-sm border border-border px-6 text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-foreground hover:border-nook-green hover:text-nook-green"
          >
            Ver unidades
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-5">
      <div className="max-w-md">
        <h1 className="display text-4xl">Algo saiu do lugar</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Não conseguimos carregar esta página. Tente novamente ou volte para o início.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex h-12 items-center rounded-sm bg-nook-green px-6 text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-nook-green-foreground"
          >
            Tentar de novo
          </button>
          <a
            href="/"
            className="inline-flex h-12 items-center rounded-sm border border-border px-6 text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-foreground"
          >
            Ir para o início
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Nook Gym" },
      { name: "theme-color", content: "#000000" },
      { name: "google-site-verification", content: "ggjDODugWakfPLENjIVjG4LCrGy9X4ySJ1sr-aMfA3o" },
      { property: "og:site_name", content: "Nook Gym" },
      { property: "og:locale", content: "pt_BR" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", href: "/favicon.png", type: "image/png", sizes: "64x64" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "preload",
        as: "style",
        href: "https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@300;400;500;600;700&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@300;400;500;600;700&display=swap",
        media: "print",
      },
    ],
    scripts: [
      {
        children: `(function(){function f(){var l=document.querySelectorAll('link[rel="stylesheet"][media="print"][href*="fonts.googleapis.com"]');for(var i=0;i<l.length;i++){l[i].media='all';}}if(document.readyState!=='loading'){f();}else{document.addEventListener('DOMContentLoaded',f);}})();`,
      },
      {
        children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=window.gtag||gtag;
try{var s=JSON.parse(localStorage.getItem('nook.cookie-consent')||'null');var a=s&&s.analytics?'granted':'denied';var m=s&&s.marketing?'granted':'denied';}catch(e){var a='denied',m='denied';}
gtag('consent','default',{analytics_storage:a,ad_storage:m,ad_user_data:m,ad_personalization:m,functionality_storage:'granted',security_storage:'granted'});`,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": `${BRAND.site}/#organization`,
              name: BRAND.name,
              url: BRAND.site,
              slogan: BRAND.tagline,
              sameAs: BRAND.sameAs,
            },
            {
              "@type": "WebSite",
              "@id": `${BRAND.site}/#website`,
              name: BRAND.name,
              url: BRAND.site,
              inLanguage: "pt-BR",
              publisher: { "@id": `${BRAND.site}/#organization` },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function AppPageAwareCookieBanner() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (pathname === "/excluir-conta" || pathname === "/suporte") return null;
  return <CookieBanner />;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <Header />
      <main id="conteudo">
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
      </main>
      <Footer />
      <TrialDialog />
      <AppPageAwareCookieBanner />
      <AnalyticsTracker />
    </QueryClientProvider>
  );
}
